import { useQuery } from '@apollo/client';
import { createError } from '@common/neo.tax';

import { GetAllFiscalYearsQuery, GetAllFiscalYearsQueryVariables, GET_ALL_FISCAL_YEARS } from '../graphql';
import { useCurrentCompanyIdOptional } from './useCurrentCompanyId';

export type FiscalYear = GetAllFiscalYearsQuery['getCompany']['fiscalYears'][number];

export type UseFiscalYears =
  | {
      fiscalYears: FiscalYear[];
      loading: false;
    }
  | {
      fiscalYears: null;
      loading: true;
    };

export const useAllFiscalYears = ({ companyId, skip }: { skip?: boolean; companyId?: string } = {}): UseFiscalYears => {
  const companyIdParam = useCurrentCompanyIdOptional();

  const companyIdToUse = companyId ? companyId : companyIdParam;
  const shouldSkip = skip || !companyIdToUse;

  const { data, loading } = useQuery<GetAllFiscalYearsQuery, GetAllFiscalYearsQueryVariables>(
    GET_ALL_FISCAL_YEARS,
    shouldSkip ? { skip: true } : { variables: { companyId: companyIdToUse } },
  );

  if (loading) {
    return { fiscalYears: null, loading: true };
  }

  if (shouldSkip) {
    return { fiscalYears: [], loading: false };
  }

  if (!data) {
    throw createError('No data returned from useAllFiscalYears');
  }

  return { fiscalYears: data.getCompany.fiscalYears, loading: false };
};

export const useAllFiscalYearsOrFail = (): FiscalYear[] => {
  const { fiscalYears } = useAllFiscalYears();
  if (!fiscalYears) {
    throw createError(
      'FiscalYearsNotLoaded',
      'Fiscal years not loaded when the component is rendered. Add waiting on the above hook to the route loader guard.',
    );
  }
  return fiscalYears;
};
